import { useRef, useState } from 'react';
import { VariableSuggestion } from './variables';

interface ExpressionInputProps {
    value: string;
    onChange: (value: string) => void;
    suggestions: VariableSuggestion[];
    placeholder?: string;
    multiline?: boolean;
}

/**
 * A JSONata expression field with upstream-variable autocomplete. Typing shows
 * matching variable paths from prior steps; clicking one inserts it at the
 * caret. Plain literals (quoted strings, numbers) are supported by JSONata.
 */
export default function ExpressionInput({
    value,
    onChange,
    suggestions,
    placeholder,
    multiline,
}: ExpressionInputProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

    const filtered = suggestions
        .filter((s) =>
            query.length === 0
                ? true
                : s.path.toLowerCase().includes(query.toLowerCase()),
        )
        .slice(0, 12);

    const insert = (path: string) => {
        const el = inputRef.current;
        if (!el) {
            onChange(value + path);
            return;
        }
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;
        const next = value.slice(0, start) + path + value.slice(end);
        onChange(next);
        // Restore focus after the state update flushes.
        requestAnimationFrame(() => {
            el.focus();
            const caret = start + path.length;
            el.setSelectionRange(caret, caret);
        });
    };

    const sharedProps = {
        ref: inputRef as any,
        value,
        placeholder: placeholder ?? 'JSONata expression, e.g. trigger.data.email',
        className:
            'w-full rounded-md border border-gray-300 px-2 py-1 text-sm font-mono focus:border-blue-500 focus:outline-none',
        onChange: (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
            onChange(e.target.value);
            // Use the trailing token (after the last separator) as the filter.
            const token = e.target.value.split(/[\s(,]/).pop() ?? '';
            setQuery(token);
        },
        onFocus: () => setOpen(true),
        onBlur: () => setTimeout(() => setOpen(false), 150),
    };

    return (
        <div className="relative">
            {multiline ? (
                <textarea {...sharedProps} rows={3} />
            ) : (
                <input {...sharedProps} />
            )}
            {open && filtered.length > 0 && (
                <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {filtered.map((s) => (
                        <button
                            key={s.path}
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                insert(s.path);
                            }}
                            className="flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-blue-50"
                        >
                            <span className="font-mono text-gray-800">
                                {s.path}
                            </span>
                            <span className="ml-2 truncate text-gray-400">
                                {s.source}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
